import { createSelector } from '@reduxjs/toolkit';
import { FeatureSelectorWithFallback } from '@store/storeTypes';
import { RootState } from '@store/types';
import { AppointmentsState } from './slice';
import { ViewMode, CalendarDateRange } from '../types';

// Empty state fallback for when feature is not loaded - only client state
const emptyState: AppointmentsState = {
  viewMode: 'week',
  displayMode: 'calendar',
  currentDate: new Date().toISOString(),
  calendarGranularity: {
    step: 15,
    timeslots: 4,
  },
  isSidebarOpen: false,
  editingAppointmentId: null,
  isTutorialMode: false,
  mockAppointments: [],
  orgScheduleFilters: {
    isOrgViewActive: false,
    selectedProviderIds: null,
    selectedPatientIds: [],
  },
};

// Base selector with fallback
export const selectAppointmentsState: FeatureSelectorWithFallback<
  AppointmentsState,
  typeof emptyState
> = (state: RootState) => state.appointments || emptyState;

// View state selectors (client UI state)
export const selectViewMode = createSelector([selectAppointmentsState], state => state.viewMode);

export const selectDisplayMode = createSelector([selectAppointmentsState], state => state.displayMode);

export const selectCurrentDate = createSelector(
  [selectAppointmentsState],
  state => new Date(state.currentDate),
);

export const selectCalendarGranularity = createSelector(
  [selectAppointmentsState],
  state => state.calendarGranularity,
);

// UI interaction state selectors (client UI state)
export const selectIsSidebarOpen = createSelector(
  [selectAppointmentsState],
  state => state.isSidebarOpen,
);

export const selectEditingAppointmentId = createSelector(
  [selectAppointmentsState],
  state => state.editingAppointmentId,
);

// Tutorial mode selectors (client application state)
export const selectIsTutorialMode = createSelector(
  [selectAppointmentsState],
  state => state.isTutorialMode,
);

export const selectMockAppointments = createSelector(
  [selectAppointmentsState],
  state => state.mockAppointments,
);

// Org schedule filter selectors (client UI state)
export const selectOrgScheduleFilters = createSelector(
  [selectAppointmentsState],
  state => state.orgScheduleFilters,
);

export const selectIsOrgViewActive = createSelector(
  [selectOrgScheduleFilters],
  filters => filters.isOrgViewActive,
);

export const selectSelectedProviderIds = createSelector(
  [selectOrgScheduleFilters],
  filters => filters.selectedProviderIds,
);

export const selectSelectedPatientIds = createSelector(
  [selectOrgScheduleFilters],
  filters => filters.selectedPatientIds,
);

export const selectHasActiveOrgFilters = createSelector(
  [selectOrgScheduleFilters],
  filters =>
    (filters.selectedProviderIds !== null && filters.selectedProviderIds.length > 0) ||
    filters.selectedPatientIds.length > 0,
);

// Computed selectors for client state

// Calculate current date range based on view mode and current date (pure client computation)
export const selectCurrentDateRange = createSelector(
  [selectViewMode, selectCurrentDate],
  (viewMode: ViewMode, currentDate: Date): CalendarDateRange => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);

    if (viewMode === 'day') {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    } else if (viewMode === 'week') {
      // Start from Sunday of the current week
      const day = currentDate.getDay();
      start.setDate(currentDate.getDate() - day);
      start.setHours(0, 0, 0, 0);

      // End on Saturday of the current week
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else if (viewMode === 'month') {
      // Start from the 1st day of the month
      start.setDate(1);
      start.setHours(0, 0, 0, 0);

      // End on the last day of the month
      end.setFullYear(currentDate.getFullYear());
      end.setMonth(currentDate.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
    }

    // Return ISO strings for stable memoization
    return {
      start: start.toISOString(),
      end: end.toISOString()
    };
  },
);

// Combined client state selectors for UI components

// Calendar view state for UI
export const selectCalendarViewState = createSelector(
  [selectViewMode, selectDisplayMode, selectCurrentDate, selectCurrentDateRange],
  (viewMode, displayMode, currentDate, currentDateRange) => ({
    viewMode,
    displayMode,
    currentDate,
    currentDateRange,
  }),
);

// Note: Server data filtering utilities will be moved to RTK Query hooks
// Functions like selectFilteredAppointments, makeSelectAppointmentsForDate, etc.
// will be implemented as RTK Query selectFromResult transforms or utility functions
